import React, { useCallback, useEffect, useRef, useState } from 'react';

import ChangeOrderReferenceCard from '../../changeOrder/cards/ChangeOrderReferenceCard';
import { ChangeOrder } from '../../changeOrder/ChangeOrder.interface';
import { useTaskEntityFields } from '../../workflow/Workflow.hooks';
import {
  useFindTrainingChangeOrders,
  useUpdateTraining
} from '../Training.hooks';
import { BaseTrainingCardProps } from '../Training.interface';

export interface TrainingAssignChangeOrdersCardProps
  extends BaseTrainingCardProps {
  disabled?: boolean;
}

const TrainingAssignChangeOrdersCard: React.FC<
  TrainingAssignChangeOrdersCardProps
> = props => {
  const { training, onChange, onError, onUpdate } = props;
  const { isFieldDisabled } = useTaskEntityFields(training?.task);
  const disabled = props.disabled || isFieldDisabled('changeOrders');
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  const onUpdateRef = useRef(onUpdate);
  onUpdateRef.current = onUpdate;
  const [order, setOrder] = useState('changeOrder.id');
  const [direction, setDirection] = useState<'ASC' | 'DESC'>('DESC');

  const {
    training: updatedTraining,
    isLoading: isUpdating,
    error: updateError,
    patch
  } = useUpdateTraining(training.id);
  const {
    changeOrders,
    isLoading: isFetching,
    error: fetchError,
    findChangeOrders
  } = useFindTrainingChangeOrders(training.id);
  const isLoading = isFetching || isUpdating;

  const handleTrainingUpdate = useCallback(
    (changeOrders: ChangeOrder[]) => {
      patch({
        changeOrders: changeOrders.map(({ id }) => {
          return { id } as ChangeOrder;
        })
      });
    },
    [patch]
  );

  const getChangeOrders = useCallback(() => {
    findChangeOrders({ order, direction });
  }, [order, direction, findChangeOrders]);

  useEffect(() => {
    getChangeOrders();
  }, [training, getChangeOrders]);

  useEffect(() => {
    if (updatedTraining) {
      onChangeRef.current?.(updatedTraining);
      onUpdateRef.current?.();
    }
  }, [updatedTraining]);

  useEffect(() => {
    if (updateError) {
      onErrorRef.current?.(updateError);
    }
  }, [updateError]);

  return (
    <ChangeOrderReferenceCard
      items={changeOrders}
      disabled={disabled}
      direction={direction}
      isLoading={isLoading}
      error={fetchError}
      isActive={column => column === order}
      handleSort={column => {
        setOrder(column);
        setDirection(direction === 'ASC' ? 'DESC' : 'ASC');
      }}
      onAssign={handleTrainingUpdate}
      onRemove={item => {
        const updated = (changeOrders || []).filter(
          changeOrder => changeOrder.id !== item.id
        );
        handleTrainingUpdate(updated);
      }}
    />
  );
};

export default TrainingAssignChangeOrdersCard;
